/**
 * @file 校区新建 / 编辑弹窗
 * @description Dialog 表单：名称、主账号、密码、点数。
 *
 * @whenToUse 校区管理页创建与编辑。
 * @whenNotToUse 其他业务表单弹窗。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import {
  EMPTY_CAMPUS_FORM,
  type CampusFormValues,
  type CampusRow,
} from '@/components/pages/campus-page/mock.ts'

/** 主账号：纯字母 / 纯数字 / 字母+数字，可含下划线 */
const ACCOUNT_PATTERN = /^[a-zA-Z0-9_]+$/

/**
 * 过滤主账号非法字符（仅保留字母、数字、下划线）
 * @param value 原始输入
 */
function sanitizeAccount(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, '')
}

export type CampusFormDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: CampusRow | null
  /** 提交 */
  onSubmit: (values: CampusFormValues) => void
}

/**
 * 校区创建 / 编辑 Dialog
 */
export function CampusFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: CampusFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<CampusFormValues>(EMPTY_CAMPUS_FORM)
  const [errors, setErrors] = useState<Partial<Record<keyof CampusFormValues, string>>>(
    {},
  )

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        name: editing.name,
        account: editing.account,
        password: '',
        points: editing.points,
      })
    } else {
      setValues(EMPTY_CAMPUS_FORM)
    }
    setErrors({})
  }, [open, editing])

  const setField = <K extends keyof CampusFormValues>(
    key: K,
    value: CampusFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const validate = () => {
    const next: Partial<Record<keyof CampusFormValues, string>> = {}
    if (!values.name.trim()) next.name = '请输入校区名称'
    const account = values.account.trim()
    if (!account) next.account = '请输入主账号'
    else if (!ACCOUNT_PATTERN.test(account))
      next.account = '仅支持字母、数字和下划线'
    if (!isEdit && !values.password.trim()) next.password = '请输入主账号密码'
    if (values.points < 0) next.points = '点数不能为负'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit(values)
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑校区' : '创建校区'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit}>{isEdit ? '保存' : '创建'}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="校区名称" required error={errors.name}>
          <Input
            value={values.name}
            invalid={Boolean(errors.name)}
            placeholder="请输入校区名称"
            onChange={(event) => setField('name', event.target.value)}
          />
        </FormField>
        <FormField
          label="校区主账号"
          required
          error={errors.account}
          hint="仅支持字母、数字和下划线"
        >
          <Input
            value={values.account}
            invalid={Boolean(errors.account)}
            placeholder="如 campus_01"
            autoComplete="username"
            onChange={(event) =>
              setField('account', sanitizeAccount(event.target.value))
            }
          />
        </FormField>
        <FormField
          label="主账号密码"
          error={errors.password}
          hint={isEdit ? '留空表示不修改密码' : undefined}
        >
          <Input
            type="password"
            value={values.password}
            invalid={Boolean(errors.password)}
            placeholder={isEdit ? '不修改请留空' : '请输入密码(默认123456)'}
            autoComplete="new-password"
            onChange={(event) => setField('password', event.target.value)}
          />
        </FormField>
        <FormField label="点数" required error={errors.points}>
          <InputNumber
            value={values.points}
            min={0}
            invalid={Boolean(errors.points)}
            onChange={(value) => setField('points', value ?? 0)}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
